import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, RefreshCw, Mail, Building2, Calendar, User, Award, AlertTriangle, ShieldCheck, Target, ChevronDown } from "lucide-react";

const ZONE_META = {
  platinum: { color: "#1A365D", icon: Award,          label: "Platinum" },
  green:    { color: "#16A34A", icon: ShieldCheck,    label: "Green" },
  amber:    { color: "#D97706", icon: Target,         label: "Amber" },
  red:      { color: "#C62828", icon: AlertTriangle,  label: "Red" },
};

export default function AdminScorecard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [zone, setZone] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/score-quiz");
      setItems(data);
    } catch (e) {
      console.error("Failed to load scorecard submissions:", e);
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase().trim();
    return items.filter((r) => {
      if (zone !== "all" && r.zone !== zone) return false;
      if (!ql) return true;
      return (
        (r.email || "").toLowerCase().includes(ql) ||
        (r.company_name || "").toLowerCase().includes(ql) ||
        (r.contact_name || "").toLowerCase().includes(ql) ||
        (r.role || "").toLowerCase().includes(ql)
      );
    });
  }, [items, q, zone]);

  const stats = useMemo(() => {
    const total = items.length;
    const avg = total ? Math.round(items.reduce((s, r) => s + (r.score || 0), 0) / total) : 0;
    const byZone = { red: 0, amber: 0, green: 0, platinum: 0 };
    items.forEach((r) => { if (byZone[r.zone] !== undefined) byZone[r.zone] += 1; });
    return { total, avg, byZone };
  }, [items]);

  const fmt = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14" data-testid="admin-scorecard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-tag">Dealer Opportunity Scorecard</div>
          <h1 className="font-heading text-4xl md:text-6xl font-black uppercase mt-1 leading-none text-[#1A365D]">Lead Magnet Submissions</h1>
          <p className="mt-2 text-slate-600">Every completed scorecard, with score, zone, and answer breakdown.</p>
        </div>
        <button onClick={load} className="btn-secondary flex items-center gap-2" data-testid="admin-scorecard-refresh">
          <RefreshCw size={14}/> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-6 gap-3">
        <StatBox label="Total" value={stats.total} accent/>
        <StatBox label="Avg Score" value={`${stats.avg}/100`}/>
        <StatBox label="Red"      value={stats.byZone.red}      dot="#C62828"/>
        <StatBox label="Amber"    value={stats.byZone.amber}    dot="#D97706"/>
        <StatBox label="Green"    value={stats.byZone.green}    dot="#16A34A"/>
        <StatBox label="Platinum" value={stats.byZone.platinum} dot="#1A365D"/>
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, company" className="ll-input pl-9" data-testid="admin-scorecard-search"/>
        </div>
        <select value={zone} onChange={(e) => setZone(e.target.value)} className="ll-select max-w-[200px]" data-testid="admin-scorecard-zone-filter">
          <option value="all">All Zones</option>
          {Object.entries(ZONE_META).map(([k, v]) => (<option key={k} value={k}>{v.label}</option>))}
        </select>
      </div>

      {/* List */}
      <div className="mt-6 space-y-3">
        {loading ? (
          <div className="py-20 text-center font-mono-ll text-sm tracking-widest text-slate-500">LOADING…</div>
        ) : filtered.length === 0 ? (
          <div className="brutal-card-flat p-12 text-center text-slate-500 font-mono-ll text-xs tracking-widest">NO SUBMISSIONS FOUND</div>
        ) : filtered.map((r) => {
          const zm = ZONE_META[r.zone] || ZONE_META.amber;
          const ZI = zm.icon;
          const isOpen = expanded === r.id;
          return (
            <div key={r.id} className="brutal-card-flat overflow-hidden" data-testid={`admin-scorecard-row-${r.id}`}>
              <button onClick={() => setExpanded(isOpen ? null : r.id)} className="w-full p-5 flex items-center gap-5 text-left hover:bg-slate-50 transition-colors">
                <div className="w-16 h-16 flex-shrink-0 flex flex-col items-center justify-center rounded" style={{ background: zm.color }}>
                  <div className="font-heading text-white text-2xl leading-none">{r.score}</div>
                  <div className="font-mono-ll text-[9px] text-white/80 mt-0.5">/{r.max_score}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono-ll text-[10px] tracking-widest uppercase px-2 py-0.5 text-white" style={{ background: zm.color }}>
                      <ZI size={10} className="inline mb-0.5 mr-1"/>{zm.label}
                    </span>
                    <span className="font-heading text-[#1A365D] text-lg truncate">{r.contact_name || "—"}</span>
                    {r.company_name && <span className="text-slate-500 text-sm">· {r.company_name}</span>}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-4 text-xs text-slate-500 font-mono-ll">
                    <a href={`mailto:${r.email}`} className="flex items-center gap-1 hover:text-[#C62828]" onClick={(e) => e.stopPropagation()}><Mail size={11}/> {r.email}</a>
                    {r.role && <span className="flex items-center gap-1"><User size={11}/> {r.role}</span>}
                    <span className="flex items-center gap-1"><Calendar size={11}/> {fmt(r.created_at)}</span>
                  </div>
                </div>
                <ChevronDown size={16} className={`text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`}/>
              </button>
              {isOpen && (
                <div className="border-t-2 border-[#1A365D] p-5 bg-slate-50">
                  <div className="label-tag mb-3">Answers</div>
                  <ul className="space-y-2">
                    {(r.answers || []).map((a) => (
                      <li key={a.q} className="flex items-start gap-3 text-sm">
                        <span className={`font-mono-ll text-[10px] tracking-widest px-2 py-1 flex-shrink-0 mt-0.5 ${a.choice === "Yes" ? "bg-green-700 text-white" : "bg-slate-300 text-slate-700"}`}>{a.choice}</span>
                        <span className="text-slate-700 flex-1">Q{a.q}. {a.question}</span>
                        <span className="font-mono-ll text-xs text-[#C62828] whitespace-nowrap">+{a.points}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatBox({ label, value, accent, dot }) {
  return (
    <div className={`p-4 border-2 ${accent ? "border-[#C62828] bg-[#C62828] text-white" : "border-[#1A365D] bg-white"}`}>
      <div className={`flex items-center gap-2 font-mono-ll text-[10px] tracking-widest uppercase ${accent ? "text-white/80" : "text-slate-500"}`}>
        {dot && <span className="w-2 h-2 rounded-full" style={{ background: dot }}/>}{label}
      </div>
      <div className={`font-heading text-3xl font-black mt-1 ${accent ? "text-white" : "text-[#1A365D]"}`}>{value}</div>
    </div>
  );
}
